<template>
    <div v-if="can">
        <settings-heading
            :title="$t('settings.reject_reasons.title')"
            :description="$t('settings.reject_reasons.description')"
            :button="$t('settings.add_new', {subject: $t('settings.reject_reasons.single')})"
            button-icon="add-circle"
            @button-click="create"
        />

        <div class="mb-48">

            <draggable-transition-group
                :options="reasons"
                label="title"
                custom-styling="mb-32"
                @editing="editing = $event"
                @deleting="deleting = $event"
                @updateOrder="updateOrder"
                @updateOptions="reasons = $event"
            ></draggable-transition-group>

            <simple-button
                class="ms-auto"
                display="secondary"
                icon="add-circle"
                look="primary"
                v-if="reasons.length > 5"
                @click="create"
            >
                {{ reasons.length ? $t('settings.add_new', {subject: $t('settings.reject_reasons.single')}) : $t('settings.add_first', {subject: $t('settings.reject_reasons.single')}) }}
            </simple-button>
        </div>

        <reject-reason-editor v-if="editing" :reason="editing" @close="stopEdit()"></reject-reason-editor>
        <reject-reason-editor v-if="creating" @close="stopCreate()"></reject-reason-editor>

        <popup-modal-confirm
            v-if="deleting"
            :message="confirmDeleteMessage(deleting)"
            :confirmText="$t('general.delete_subject', {subject: $t('settings.reject_reasons.single')})"
            :working="performingDeleteRequest"
            show-exclamation
            @confirm="deleteReason(deleting)"
            @close="cancelDelete"
        />
    </div>
    <error-403 v-else></error-403>
</template>

<script>
import SimpleButton from '../../components/ui/SimpleButton.vue';
import * as RejectReasonService from '../../services/RejectReasonService'
import RejectReason from '../../models/RejectReason';
import { normalizeJson } from '../../../shared/helpers';
import { mapState, mapActions } from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useNotificationsStore} from '../../state/modules/notifications';
import SvgIcon from '../../components/ui/SvgIcon.vue';
import PopupModalConfirm from '../../components/ui/PopupModalConfirm.vue';
import RejectReasonEditor from '../../components/ui/RejectReasonEditor.vue';
import { ADD_NOTIFICATION } from '../../state/mutation-types';
import Error403 from '../../components/ui/Error403.vue';
import DraggableTransitionGroup from "../../components/ui/DraggableTransitionGroup";
import SettingsHeading from "../../components/ui/SettingsHeading.vue";

export default {
    components: {
        SettingsHeading,
        DraggableTransitionGroup,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'reject-reason-editor': RejectReasonEditor,
        'error-403': Error403,
    },
    data: function () {
        return {
            fetchingReasons: true,
            editing: null,
            creating: false,
            deleting: null,
            performingDeleteRequest: false,
            drag: false,
            currentReason: null,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        reasons : {
            get () {
                return useRepo(RejectReason).orderBy('order').get().map(reason => {
                    return {
                        id: reason.id,
                        title: reason.title,
                        order: reason.order,
                        used: reason.used
                    };
                });
            },
            set (reasons) {
                this.resort(reasons);
            }
        },
        dragOptions () {
            return {
                animation: 200,
                group: "items",
                disabled: false,
                ghostClass: "ghost"
            };
        },
        can () {
            return this.$can('settings.applications');
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        fetchReasons () {
            return RejectReasonService.index().then(result => {
                return useRepo(RejectReason).save(normalizeJson(result.data));
            });
        },
        create () {
            this.creating = true;
        },
        stopCreate () {
            this.creating = false;
        },
        edit (reason) {
            this.editing = reason;
        },
        stopEdit () {
            this.editing = null;
        },
        confirmDelete (reason) {
            this.deleting = reason;
        },
        cancelDelete () {
            this.deleting = null;
        },
        deleteReason (reason) {
            this.performingDeleteRequest = true;

            RejectReasonService.destroy(reason.id).then(() => {
                useRepo(RejectReason).destroy(reason.id);
            }).then(() => {
                this.deleting = null;
                this.performingDeleteRequest = false;
            }).then(() => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.deleted', {subject: this.$t('settings.reject_reasons.single')}),
                });
            });
        },
        confirmDeleteMessage (reason) {
            return reason.used ?
                this.$t('settings.reject_reason.used_confirm') :
                this.$t('general.confirm_delete_subject', {subject: this.$t('settings.reject_reasons.single')});
        },
        resort (sortedReasons) {
            useRepo(RejectReason).save(sortedReasons.map(
                (reason, index) => ({ id: reason.id, order: index }))
            );
        },
        updateOrder ({evt, item, done}) {
            RejectReasonService.update(
                item.id,
                { data: { attributes: {order: evt.newDraggableIndex}}}
            ).then(result => {
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('general.order_changed', {subject: this.$t('settings.reject_reasons.title')}),
                });
            }).then(() => done());
        }
    },
    mounted () {
        this.fetchingReasons = true;

        this.fetchReasons().then(inserts => {
            this.fetchingReasons = false;
        });
    }
}
</script>
